import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { tournamentsApi } from '../api/tournaments';
import { authApi } from '../api/auth';
import { Trophy, Users, Calendar, Filter, Plus, Play, ChevronLeft, ChevronRight, BarChart3, Clock, Pencil } from 'lucide-react';
import '../styles/football-theme.css';
import '../styles/tournaments-gaming.css';
import { useLanguage } from '../i18n/LanguageContext';
import { formatDate } from '../utils/locale';
import { sortGameweekTournamentsByRarity } from '../utils/gameweekTournamentOrder';

const GAMEWEEK_FORMATION_LOCKOUT_MS = 20 * 60 * 1000;

const Tournaments = () => {
  const navigate = useNavigate();
  const [user] = useState(() => authApi.getCurrentUser());
  const [activeTournaments, setActiveTournaments] = useState([]);
  const [allActiveTournaments, setAllActiveTournaments] = useState([]);
  const [registeredTournamentIds, setRegisteredTournamentIds] = useState(new Set());
  
  // Convert Set to Array for memo comparison
  const registeredTournamentIdsArray = useMemo(() => 
    Array.from(registeredTournamentIds),
    [registeredTournamentIds]
  );
  const [filter, setFilter] = useState('all'); // all, weekly, monthly, daily
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const ITEMS_PER_PAGE = 6;
  const isLoadingRef = useRef(false);
  const userRef = useRef(user);
  const filterTimeoutRef = useRef(null);
  const { t } = useLanguage();

  // Update user ref when user changes
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const loadRegisteredTournaments = useCallback(async () => {
    if (!userRef.current?.id) return;
    
    try {
      const response = await tournamentsApi.getUserRegistered(userRef.current.id);
      if (response?.success && response.tournaments) {
        const registeredIds = new Set(response.tournaments.map(t => t.tournament_id || t.id));
        setRegisteredTournamentIds(registeredIds);
      }
    } catch (error) {
      console.error('Error loading registered tournaments:', error);
    }
  }, []);

  const loadTournaments = useCallback(async () => {
    // Prevent multiple simultaneous loads
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    
    setLoading(true);
    try {
      // Carica sempre le gameweek (tornei di tipo 'gameweek')
      const gameweekResponse = await tournamentsApi.getActive({ type: 'gameweek' });
      
      // Carica altri tornei in base al filtro
      const filters = filter !== 'all' && filter !== 'gameweek' ? { type: filter } : {};
      const otherTournamentsResponse = filter !== 'gameweek' 
        ? await tournamentsApi.getActive(filters)
        : { success: true, tournaments: [] };
      
      let allTournaments = [];
      
      if (gameweekResponse?.success) {
        const gameweeks = gameweekResponse.tournaments || [];
        console.log('🔍 [Tournaments] Gameweek tournaments received:', gameweeks.length, gameweeks);
        
        // Raggruppa le gameweek per nome base (ogni gameweek ha 5 tornei)
        const groupedGameweeks = gameweeks.reduce((acc, gw) => {
          const nameParts = gw.name.split(' - ');
          const gameweekName = nameParts.length >= 2 
            ? nameParts.slice(0, -1).join(' - ') 
            : nameParts[0];
          
          if (!acc[gameweekName]) {
            acc[gameweekName] = {
              ...gw,
              gameweekName,
              tournaments: [],
              start_date: gw.start_date,
              end_date: gw.end_date,
              status: gw.gameweek_status || gw.status,
              gameweek_status: gw.gameweek_status || gw.status
            };
          }
          acc[gameweekName].tournaments.push(gw);
          
          const hasGameweekStatus = Boolean(acc[gameweekName].gameweek_status);
          if (!hasGameweekStatus) {
            // Aggiorna lo status: se almeno un torneo è in_progress, la gameweek è in_progress
            // Altrimenti se almeno uno è open, è open, altrimenti upcoming, pending_payment o closed
            const statuses = acc[gameweekName].tournaments.map(t => t.gameweek_status || t.status);
            if (statuses.includes('in_progress')) {
              acc[gameweekName].status = 'in_progress';
            } else if (statuses.includes('open')) {
              acc[gameweekName].status = 'open';
            } else if (statuses.includes('pending_payment')) {
              acc[gameweekName].status = 'pending_payment';
            } else if (statuses.includes('closed') || statuses.includes('close')) {
              acc[gameweekName].status = 'closed';
            } else if (statuses.includes('upcoming')) {
              acc[gameweekName].status = 'upcoming';
            } else {
              // Se tutti i tournaments sono chiusi o non ci sono status, controlla le date
              const now = new Date();
              const endDate = new Date(acc[gameweekName].end_date);
              if (now > endDate) {
                acc[gameweekName].status = 'closed';
              }
            }
          }
          
          return acc;
        }, {});
        
        // Filtra solo le gameweek che hanno almeno un torneo; allinea inizio/fine alla finestra reale (stessi valori dopo sync API, utile se le righe divergono)
        const validGameweeks = Object.values(groupedGameweeks)
          .filter(gw => gw.tournaments.length > 0)
          .map((gw) => {
            let bestStart = null;
            let bestEnd = null;
            let bestStartTs = Infinity;
            let bestEndTs = -Infinity;
            for (const sub of gw.tournaments) {
              if (sub.start_date) {
                const ts = new Date(sub.start_date).getTime();
                if (!Number.isNaN(ts) && ts < bestStartTs) {
                  bestStartTs = ts;
                  bestStart = sub.start_date;
                }
              }
              if (sub.end_date) {
                const ts = new Date(sub.end_date).getTime();
                if (!Number.isNaN(ts) && ts > bestEndTs) {
                  bestEndTs = ts;
                  bestEnd = sub.end_date;
                }
              }
            }
            if (bestStart != null) gw.start_date = bestStart;
            if (bestEnd != null) gw.end_date = bestEnd;
            gw.tournaments = sortGameweekTournamentsByRarity(gw.tournaments);
            return gw;
          });
        validGameweeks.sort((a, b) => {
          const ta = new Date(a.start_date || 0).getTime();
          const tb = new Date(b.start_date || 0).getTime();
          if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
          if (Number.isNaN(ta)) return 1;
          if (Number.isNaN(tb)) return -1;
          return tb - ta;
        });
        console.log('🔍 [Tournaments] Grouped gameweeks:', validGameweeks.length, validGameweeks.map(gw => ({ name: gw.gameweekName, status: gw.status, tournaments: gw.tournaments.length })));
        
        // Aggiungi le gameweek raggruppate solo se il filtro è 'all' o 'gameweek'
        if (filter === 'all' || filter === 'gameweek') {
          allTournaments = validGameweeks;
          console.log('🔍 [Tournaments] Final allTournaments:', allTournaments.length, allTournaments.map(gw => ({ name: gw.gameweekName, status: gw.status })));
        }
      }
      
      // Aggiungi altri tornei (non gameweek) solo se il filtro è 'all' o un tipo specifico (non gameweek)
      // Escludi i tornei mensili/semestrali/annuali (vanno nella sezione Rankings)
      if (otherTournamentsResponse?.success && filter !== 'gameweek') {
        const otherTournaments = (otherTournamentsResponse.tournaments || []).filter(
          t => t.type !== 'gameweek' && t.type !== 'monthly' && t.type !== 'semestral' && t.type !== 'annual'
        );
        if (filter === 'all') {
          allTournaments = [...allTournaments, ...otherTournaments];
        } else {
          // Se è un filtro specifico (private, thematic), mostra solo quelli
          // Escludi monthly, semestral, annual
          allTournaments = otherTournaments;
        }
      }
      
      setAllActiveTournaments(allTournaments);
      const start = currentPage * ITEMS_PER_PAGE;
      setActiveTournaments(allTournaments.slice(start, start + ITEMS_PER_PAGE));
    } catch (error) {
      console.error('Error loading tournaments:', error);
      setActiveTournaments([]);
      setAllActiveTournaments([]);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [filter, currentPage]);

  useEffect(() => {
    setCurrentPage(0);
    loadTournaments();
    if (user?.id) {
      loadRegisteredTournaments();
    }
  }, [filter, loadTournaments, loadRegisteredTournaments, user?.id]);

  // Dopo modifiche admin su un’altra tab, al ritorno qui ricarica così date/countdown si allineano
  useEffect(() => {
    let lastFetch = 0;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const t = Date.now();
      if (t - lastFetch < 4000) return;
      lastFetch = t;
      loadTournaments();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loadTournaments]);

  useEffect(() => {
    if (allActiveTournaments.length > 0) {
      const start = currentPage * ITEMS_PER_PAGE;
      setActiveTournaments(allActiveTournaments.slice(start, start + ITEMS_PER_PAGE));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const getTournamentTypeColor = useCallback((type) => {
    switch (type) {
      case 'daily': return 'from-green-400 to-emerald-500';
      case 'weekly': return 'from-blue-400 to-cyan-500';
      case 'monthly': return 'from-purple-400 to-pink-500';
      default: return 'from-yellow-400 to-orange-500';
    }
  }, []);

  const getStatusLabel = useCallback((status) => {
    switch (status) {
      case 'in_progress':
        return t('tournaments.statusInProgress');
      case 'pending_payment':
        return t('tournaments.statusPendingPayment');
      case 'close':
      case 'closed':
        return t('tournaments.statusClosed');
      case 'upcoming':
        return t('tournaments.statusUpcoming');
      case 'open':
        return t('tournaments.statusOpen');
      default:
        return String(status || '').toUpperCase();
    }
  }, [t]);

  const getStatusBadgeClass = useCallback((status) => {
    switch (status) {
      case 'in_progress':
        return 'bg-yellow-500 text-black';
      case 'pending_payment':
        return 'bg-purple-600 text-white text-xs px-2 py-0.5';
      case 'close':
      case 'closed':
        return 'bg-gray-500 text-white';
      case 'upcoming':
        return 'bg-blue-600 text-white';
      case 'open':
        return 'bg-green-600 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  }, []);

  return (
    <div className="tournaments-container min-h-screen pt-20 pb-8 px-4 sm:px-6 lg:px-8">
      {/* Tournaments Background */}
      <div 
        className="tournaments-background"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%), url('/images/tournamentsbg.png')`
        }}
      ></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-bold text-[#1e293b] flex items-center">
            <Trophy className="w-10 h-10 mr-3 text-yellow-400" />
            {t('tournaments.title')}
          </h1>
          {user && (
            <button
              type="button"
              disabled
              className="btn-football flex items-center space-x-2 opacity-60 cursor-not-allowed"
              title="Coming soon"
            >
              <Plus className="w-5 h-5" />
              <span>Coming soon</span>
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="football-card glass-football p-6 mb-6">
          <div className="flex items-center space-x-2 mb-4">
            <Filter className="w-5 h-5 text-yellow-400" />
            <h2 className="text-xl font-bold text-white">{t('tournaments.filterTitle')}</h2>
          </div>
          <div className="flex space-x-2 flex-wrap">
            {['all', 'gameweek', 'private', 'thematic'].map((type) => (
              <button
                key={type}
                onClick={() => {
                  // Clear any pending filter change
                  if (filterTimeoutRef.current) {
                    clearTimeout(filterTimeoutRef.current);
                  }
                  // Update filter immediately for UI responsiveness
                  setFilter(type);
                }}
                className={`px-4 py-2 rounded-lg font-semibold capitalize transition-all ${
                  filter === type
                    ? 'bg-yellow-400 text-black'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {type === 'gameweek'
                  ? t('tournaments.filterGameweek')
                  : type === 'all'
                    ? t('tournaments.filterAll')
                    : t(`tournaments.filter${type.charAt(0).toUpperCase()}${type.slice(1)}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Tournaments Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="football-spinner mx-auto"></div>
            <p className="mt-4 text-gray-400">{t('tournaments.loading')}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activeTournaments.map((tournament) => (
              <TournamentCard
                key={tournament.id || tournament.gameweekName}
                tournament={tournament}
                user={user}
                registeredTournamentIds={registeredTournamentIdsArray}
                getTournamentTypeColor={getTournamentTypeColor}
                getStatusLabel={getStatusLabel}
                getStatusBadgeClass={getStatusBadgeClass}
              />
            ))}
          </div>
        )}

        {!loading && activeTournaments.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            {filter === 'gameweek' 
              ? t('tournaments.emptyGameweeks')
              : t('tournaments.emptyTournaments')
            }
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && allActiveTournaments.length > ITEMS_PER_PAGE && (
          <div className="flex items-center justify-center space-x-4 mt-6">
            <button
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
              className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                currentPage === 0
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-yellow-400 text-black hover:bg-yellow-500'
              }`}
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              {t('common.previous')}
            </button>
            <span className="text-white/90 text-sm font-medium">
              {t('common.pageOf', { page: currentPage + 1, total: Math.ceil(allActiveTournaments.length / ITEMS_PER_PAGE) })}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(Math.ceil(allActiveTournaments.length / ITEMS_PER_PAGE) - 1, currentPage + 1))}
              disabled={currentPage >= Math.ceil(allActiveTournaments.length / ITEMS_PER_PAGE) - 1}
              className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                currentPage >= Math.ceil(allActiveTournaments.length / ITEMS_PER_PAGE) - 1
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-yellow-400 text-black hover:bg-yellow-500'
              }`}
            >
              {t('common.next')}
              <ChevronRight className="w-5 h-5 ml-1" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/** Format ms as "2d 5h 30m 15s" (compact) */
function formatCountdown(ms) {
  if (ms <= 0) return null;
  const sec = Math.floor(ms / 1000) % 60;
  const min = Math.floor(ms / 60000) % 60;
  const hr = Math.floor(ms / 3600000) % 24;
  const day = Math.floor(ms / 86400000);
  const parts = [];
  if (day > 0) parts.push(`${day}d`);
  if (hr > 0 || parts.length) parts.push(`${hr}h`);
  if (min > 0 || parts.length) parts.push(`${min}m`);
  parts.push(`${sec}s`);
  return parts.join(' ');
}

function getFormationLockState(startDate, now) {
  if (!startDate) return { isLocked: false, msUntilLock: null };
  const startTs = new Date(startDate).getTime();
  const nowTs = now instanceof Date ? now.getTime() : now;
  if (Number.isNaN(startTs) || Number.isNaN(nowTs)) {
    return { isLocked: false, msUntilLock: null };
  }
  const deadlineTs = startTs - GAMEWEEK_FORMATION_LOCKOUT_MS;
  if (nowTs >= startTs) {
    return { isLocked: false, msUntilLock: null };
  }
  if (nowTs >= deadlineTs) {
    return { isLocked: true, msUntilLock: 0 };
  }
  return { isLocked: false, msUntilLock: deadlineTs - nowTs };
}

const TournamentCountdown = React.memo(({ startDate, endDate, isClosed, isGameweek, t }) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : null;
  if (isClosed || (end && now >= end)) {
    return (
      <div className="flex items-center mt-2 pt-2 border-t border-white/10">
        <Clock className="w-4 h-4 mr-2 text-gray-500" />
        <span className="text-gray-500 font-medium">{t('tournaments.countdownEnded')}</span>
      </div>
    );
  }
  if (now < start) {
    const ms = start - now;
    const str = formatCountdown(ms);
    if (!str) return null;
    const formationLock = isGameweek ? getFormationLockState(startDate, now) : { isLocked: false, msUntilLock: null };
    const formationLockStr = formationLock.msUntilLock ? formatCountdown(formationLock.msUntilLock) : null;
    return (
      <>
        {isGameweek && (
          <div className={`flex items-center mt-2 pt-2 border-t tournaments-countdown ${formationLock.isLocked ? 'border-red-500/30' : 'border-fuchsia-500/30'}`}>
            <Clock className={`w-4 h-4 mr-2 ${formationLock.isLocked ? 'text-red-400' : 'text-fuchsia-400'}`} />
            <span className={`${formationLock.isLocked ? 'text-red-300' : 'text-fuchsia-300'} font-semibold`}>
              {formationLock.isLocked
                ? t('tournaments.countdownFormationLocked')
                : (<>{t('tournaments.countdownFormationLocksIn')}: <span className="text-white tabular-nums">{formationLockStr}</span></>)}
            </span>
          </div>
        )}
        <div className="flex items-center mt-2 pt-2 border-t border-cyan-500/30 tournaments-countdown">
          <Clock className="w-4 h-4 mr-2 text-cyan-400" />
          <span className="text-cyan-300 font-semibold">
            {t('tournaments.countdownStartsIn')}: <span className="text-white tabular-nums">{str}</span>
          </span>
        </div>
      </>
    );
  }
  if (end && now < end) {
    const ms = end - now;
    const str = formatCountdown(ms);
    if (!str) return null;
    return (
      <div className="flex items-center mt-2 pt-2 border-t border-amber-500/30 tournaments-countdown">
        <Clock className="w-4 h-4 mr-2 text-amber-400 animate-pulse" />
        <span className="text-amber-300 font-semibold">
          {t('tournaments.countdownEndsIn')}: <span className="text-white tabular-nums">{str}</span>
        </span>
      </div>
    );
  }
  return null;
});

TournamentCountdown.displayName = 'TournamentCountdown';

const TournamentCard = React.memo(({ tournament, user, registeredTournamentIds, getTournamentTypeColor, getStatusLabel, getStatusBadgeClass }) => {
  const navigate = useNavigate();
  const [showTournaments, setShowTournaments] = useState(false);
  const [nowTs, setNowTs] = useState(() => Date.now());
  const { t, locale } = useLanguage();

  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const handleEnter = useCallback(async (tournamentId) => {
    if (!user) {
      navigate('/login');
      return;
    }
    navigate(`/tournaments/${tournamentId}/enter`);
  }, [user, navigate]);

  const isRegistered = useCallback((tournamentId) => {
    return registeredTournamentIds.includes(tournamentId);
  }, [registeredTournamentIds]);

  // Se è una gameweek raggruppata, mostra informazioni aggregate - memoizzate
  const isGameweekGroup = useMemo(() => 
    tournament.tournaments && tournament.tournaments.length > 0,
    [tournament.tournaments]
  );
  
  const totalParticipants = useMemo(() => 
    isGameweekGroup
      ? tournament.tournaments.reduce((sum, t) => sum + (parseInt(t.participants) || 0), 0)
      : (tournament.participants || 0),
    [isGameweekGroup, tournament.tournaments, tournament.participants]
  );

  const totalPrizePool = useMemo(() =>
    isGameweekGroup
      ? tournament.tournaments.reduce((sum, t) => sum + (parseFloat(t.prize_pool) || 0), 0)
      : (parseFloat(tournament.prize_pool) || 0),
    [isGameweekGroup, tournament.tournaments, tournament.prize_pool]
  );
  
  const displayName = useMemo(() => 
    isGameweekGroup ? tournament.gameweekName : tournament.name,
    [isGameweekGroup, tournament.gameweekName, tournament.name]
  );

  const isInProgress = useMemo(() =>
    tournament.status === 'in_progress',
    [tournament.status]
  );

  // pending_payment = gameweek ended, scores final, prizes not yet paid - treat like closed for UI (show scores, hide Entra/Edit)
  const isClosed = useMemo(() =>
    tournament.status === 'closed' || tournament.status === 'close' || tournament.status === 'pending_payment',
    [tournament.status]
  );

  const isFormationLocked = useCallback((startDate) => {
    return getFormationLockState(startDate, nowTs).isLocked;
  }, [nowTs]);

  return (
    <div className={`football-card glass-football p-6 card-hover ${isInProgress ? 'border-2 border-yellow-400 shadow-lg shadow-yellow-400/20' : isClosed ? 'border-2 border-gray-500 opacity-75' : ''}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <span className={`px-3 py-1 bg-gradient-to-r ${getTournamentTypeColor(tournament.type)} rounded-full text-sm font-semibold text-white capitalize`}>
            {tournament.type}
          </span>
          {tournament.status && (
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusBadgeClass(tournament.status)} ${isInProgress ? 'animate-pulse' : ''}`}>
              {getStatusLabel(tournament.status)}
            </span>
          )}
        </div>
        <div className="flex items-center text-gray-400 text-sm">
          <Users className="w-4 h-4 mr-1" />
          {totalParticipants} / {tournament.max_participants || '∞'}
        </div>
      </div>

      {totalPrizePool > 0 && !tournament.hide_prize && (
        <div className="flex items-center text-sm mb-2 font-semibold tournaments-prize-gold tournaments-montepremi-card">
          <span className="text-lg mr-1 tournaments-prize-gold-icon" aria-hidden>€</span>
          {t('tournaments.prizePool')}: {totalPrizePool.toFixed(2)} DIOS
        </div>
      )}

      <h3 className="text-2xl font-bold text-white mb-2">{displayName}</h3>
      
      {/* Show Check Scores button for closed gameweeks */}
      {isGameweekGroup && isClosed && tournament.tournaments.length > 0 && (
        <div className="mb-3">
          <Link
            to={`/rankings?tournamentId=${tournament.tournaments[0].id}`}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-semibold transition-all"
            title={t('tournaments.viewGameweekScores')}
          >
            <BarChart3 className="w-5 h-5" />
            <span>{t('tournaments.checkScores')}</span>
          </Link>
        </div>
      )}
      
      {isGameweekGroup && (
        <div className="mb-3">
          <button
            onClick={() => setShowTournaments(!showTournaments)}
            className="w-full p-3 bg-gray-700/50 rounded-lg hover:bg-gray-700/70 transition-all text-left"
          >
            <p className="text-sm text-gray-300 mb-1">
              {showTournaments ? '▼' : '▶'} {t('tournaments.availableTournaments', { count: tournament.tournaments.length })}
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              {tournament.tournaments.map((item) => (
                <span
                  key={item.id}
                  className="px-2 py-1 bg-gray-600 rounded text-xs text-gray-300 capitalize"
                >
                  {item.rarity_restriction || 'all'}
                </span>
              ))}
            </div>
          </button>
          
          {showTournaments && (
            <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
              {tournament.tournaments.map((item) => (
                <div
                  key={item.id}
                  className="p-3 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-yellow-500/50 transition-all"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-white capitalize">
                      {item.rarity_restriction || t('tournaments.rarityBasic')}
                    </span>
                    <span className="text-xs text-gray-400">
                      {t('tournaments.participants', { count: item.participants || 0 })}
                    </span>
                  </div>
                  {(parseFloat(item.prize_pool) || 0) > 0 && !tournament.hide_prize && (
                    <div className="flex items-center text-xs mb-2 font-semibold tournaments-prize-gold">
                      <span className="mr-1 tournaments-prize-gold-icon" aria-hidden>€</span>
                      {t('tournaments.prize')}: {(parseFloat(item.prize_pool) || 0).toFixed(2)} DIOS
                    </div>
                  )}
                  {item.status && (
                    <div className="mb-2">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadgeClass(item.status)}`}>
                        {getStatusLabel(item.status)}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      {item.buy_in_amount === 0
                        ? t('tournaments.free')
                        : t('tournaments.buyInAmount', { amount: item.buy_in_amount })}
                    </span>
                    {isRegistered(item.id) ? (
                      <div className="flex items-center gap-2">
                        <span className="px-3 py-1 bg-green-500 rounded text-xs font-semibold text-white">
                          {t('tournaments.registered')}
                        </span>
                        {isClosed ? (
                          <Link
                            to={`/rankings?tournamentId=${item.id}`}
                            className="px-3 py-1 rounded text-xs font-semibold bg-blue-500 hover:bg-blue-600 text-white flex items-center space-x-1"
                            title={t('tournaments.viewTournamentScores')}
                          >
                            <BarChart3 className="w-3 h-3" />
                            <span>{t('tournaments.checkScores')}</span>
                          </Link>
                        ) : (item.status !== 'in_progress' && item.status !== 'closed' && item.status !== 'close' && item.status !== 'pending_payment' && !isFormationLocked(item.start_date)) ? (
                          <Link
                            to={`/tournaments/${item.id}/formation`}
                            className="px-3 py-1 rounded text-xs font-semibold bg-yellow-500 hover:bg-yellow-600 text-black flex items-center gap-1"
                            title={t('tournaments.editFormation')}
                          >
                            <Pencil className="w-3 h-3" />
                            {t('tournaments.editFormation')}
                          </Link>
                        ) : null}
                      </div>
                    ) : isClosed ? (
                      <Link
                        to={`/rankings?tournamentId=${item.id}`}
                        className="px-3 py-1 rounded text-xs font-semibold bg-blue-500 hover:bg-blue-600 text-white flex items-center space-x-1"
                        title={t('tournaments.viewTournamentScores')}
                      >
                        <BarChart3 className="w-3 h-3" />
                        <span>{t('tournaments.checkScores')}</span>
                      </Link>
                    ) : isFormationLocked(item.start_date) ? null : (
                      <button
                        onClick={() => handleEnter(item.id)}
                        disabled={isInProgress}
                        className={`px-3 py-1 rounded text-xs font-semibold ${
                          isInProgress
                            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                            : 'bg-yellow-500 hover:bg-yellow-600 text-black'
                        }`}
                        title={isInProgress ? t('tournaments.entriesClosed') : ''}
                      >
                        {t('tournaments.enter')}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      <div className="space-y-2 mb-4 text-sm text-gray-300">
        <div className="flex items-center">
          <Calendar className="w-4 h-4 mr-2" />
          <span className="!text-white">{t('tournaments.starts')}: {formatDate(tournament.start_date, { dateStyle: 'medium', timeStyle: 'short' }, locale)}</span>
        </div>
        {tournament.end_date && (
          <div className="flex items-center">
            <Calendar className="w-4 h-4 mr-2" />
            <span className="!text-white">{t('tournaments.ends')}: {formatDate(tournament.end_date, { dateStyle: 'medium', timeStyle: 'short' }, locale)}</span>
          </div>
        )}
        {tournament.start_date && (
          <TournamentCountdown startDate={tournament.start_date} endDate={tournament.end_date} isClosed={isClosed} isGameweek={isGameweekGroup || tournament.type === 'gameweek'} t={t} />
        )}
        {!isGameweekGroup && tournament.buy_in_amount > 0 && (
          <div className="flex items-center">
            <span className="mr-2 text-yellow-400 font-semibold" aria-hidden>€</span>
            <span className="text-yellow-400 font-semibold">{t('tournaments.buyIn')}: {tournament.buy_in_amount} DIOS</span>
          </div>
        )}
        {isGameweekGroup && !showTournaments && (
          <div className="text-xs text-gray-400">
            {t('tournaments.gameweekPricing')}
          </div>
        )}
        {!isGameweekGroup && tournament.rarity_restriction && (
          <div className="text-xs text-gray-400">
            {t('tournaments.rarityLabel')}: {tournament.rarity_restriction}
          </div>
        )}
      </div>

      {!isGameweekGroup && (
        isRegistered(tournament.id) ? (
          <div className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-green-500/20 border-2 border-green-500 rounded-lg">
            <span className="text-green-400 font-semibold">{t('tournaments.registered')}</span>
            {!isClosed && !isInProgress && !isFormationLocked(tournament.start_date) && (
              <Link
                to={`/tournaments/${tournament.id}/formation`}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-yellow-500 hover:bg-yellow-600 text-black flex items-center gap-2"
                title={t('tournaments.editFormation')}
              >
                <Pencil className="w-4 h-4" />
                {t('tournaments.editFormation')}
              </Link>
            )}
          </div>
        ) : isClosed ? (
          <Link
            to={`/rankings?tournamentId=${tournament.id}`}
            className="w-full flex items-center justify-center space-x-2 btn-football bg-blue-600 hover:bg-blue-700"
            title={t('tournaments.viewTournamentScores')}
          >
            <BarChart3 className="w-4 h-4" />
            <span>{t('tournaments.checkScores')}</span>
          </Link>
        ) : isFormationLocked(tournament.start_date) ? null : (
          <button
            onClick={() => handleEnter(tournament.id)}
            disabled={isInProgress}
            className={`w-full flex items-center justify-center space-x-2 ${
              isInProgress
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed px-4 py-3 rounded-lg'
                : 'btn-football'
            }`}
            title={isInProgress ? t('tournaments.entriesClosed') : ''}
          >
            <Play className="w-4 h-4" />
            <span>
              {isInProgress 
                ? t('tournaments.inProgressClosed')
                : tournament.type === 'gameweek' 
                  ? (tournament.buy_in_amount > 0 ? t('tournaments.joinGameweek') : t('tournaments.joinFreeGameweek'))
                  : (tournament.buy_in_amount > 0 ? t('tournaments.enterTournament') : t('tournaments.joinFree'))
              }
            </span>
          </button>
        )
      )}
    </div>
  );
});

TournamentCard.displayName = 'TournamentCard';

export default Tournaments;
